/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { api } from "../../../api/GeneratedApi";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import { ProductsAPI } from "../../../api/Products";
import { TagApi } from "../../../api/Tags";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import {
  IProduct,
  IShopifyIntegration,
  IShopifyProduct,
  ProductType,
} from "../../../generated/Api";
import { SettingsTab } from "../../../pages/settings";
import { IOption } from "../../../types/CommonTypes";
import Warning from "../../Modal/container/Warning";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import { useModal } from "../../Modal/Modal";
import { AllIntegrationsAtom } from "./Integrations";
import { ProductItem } from "./ProductItem";
import { ShopifyAPI } from "../../../api/ShopifyAPI";
import { ShopifyProductItem } from "./ShopifyProductItem";

const Container = styled.div`
  width: 1000px;
  height: fit-content;

  border: 1px solid #eaeaeabf;

  background-color: #ffffff;

  margin: 40px 0px;

  padding: 24px;

  cursor: default;
`;

const InfoColumn = styled.div`
  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 16px;

  display: flex;
  align-items: stretch;
  justify-content: space-between;

  > img {
    width: 67px;
    height: 67px;
  }
`;

const TextColumn = styled.div`
  max-width: 600px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 18px;
  color: #222222;
  font-weight: 700;
  margin-bottom: 4px;
`;

const SubTitle = styled.div`
  font-size: 16px;
  color: #707070;
`;

const FormWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: start;

  margin: 32px 0;

  grid-gap: 24px;
`;

const StyledAutocompleteSelectWrapper = styled.div`
  width: 420px;
`;

const ProductListContent = styled.div`
  border-top: 1px solid #eaeaeabf;
  padding-top: 16px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;

  width: 100%;

  grid-gap: 32px;
`;

const ProductListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  width: 100%;

  grid-gap: 16px;
`;

const FooterColumn = styled.div`
  border-top: 1px solid #eaeaeabf;

  padding-top: 16px;

  margin-top: 16px;

  display: flex;
  align-items: center;
  justify-content: start;
`;

interface Props {
  handleSelected: (vle: SettingsTab) => void;
}

export default function ShopifyProducts(props: Props): JSX.Element | null {
  const { handleSelected } = props;
  const user = useRecoilValue(UserAtom);
  const [selectedIntegrations, setSelectedIntegrations] = useState<IOption>({
    name: "",
    value: "",
  });
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<
    IOption[]
  >([]);

  const modal = useModal();

  const [tagsPool, setTagsPool] = useRecoilState(TagsAtom);

  const allIntegrations = useRecoilValue(AllIntegrationsAtom);
  const shopifyIntegration = useMemo(
    () => allIntegrations.find((i) => i.type === "SHOPIFY"),
    [allIntegrations]
  ) as IShopifyIntegration;

  const [shopifyProducts, setShopifyProducts] = useState<IShopifyProduct[]>([]);
  const [currentProductSelected, setCurrentProductSelected] =
    useState<IShopifyProduct | null>(null);

  const [allIntegrationProducts, setAllIntegrationProducts] = useState<
    IProduct[]
  >([]);

  useEffect(() => {
    if (!shopifyIntegration || shopifyIntegration.type !== "SHOPIFY")
      handleSelected(SettingsTab.INTEGRATIONS);

    (async () => {
      let { products } = await ShopifyAPI.getProducts();

      if (products) {
        let allIntegrationProducts = await getAllIntegrationProducts();
        setAllIntegrationProducts(allIntegrationProducts);

        // Filter the already created ones
        products = products.filter(
          (tp) =>
            !allIntegrationProducts.find(
              (ip) => ip.productId.toString() === tp.id.toString()
            )
        );

        setShopifyProducts(products);
        setAutoCompleteSuggestions(
          products.map((p) => ({
            name: p.title,
            value: p.id.toString(),
          }))
        );
      }
    })();
  }, [shopifyIntegration]);

  async function getAllIntegrationProducts() {
    const allIntegrationProducts = await ProductsAPI.get("SHOPIFY");
    return allIntegrationProducts;
  }

  async function getTags() {
    const tags = await TagApi.readAll({ getCampaigns: true });
    setTagsPool(tags);
  }

  useEffect(() => {
    getTags();
  }, []);

  function handleSelectedSearch(savedSearchOption: IOption) {
    const product = shopifyProducts.find(
      (p) => p.id.toString() === savedSearchOption.value
    );
    if (product) {
      setSelectedIntegrations({
        name: product.title,
        value: product.id.toString(),
      });
      setCurrentProductSelected(product);
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (currentProductSelected) {
      await ProductsAPI.create({
        productId: currentProductSelected.id.toString(),
        productName: currentProductSelected.title,

        tagIds: [],
        type: ProductType.SHOPIFY,
        userUid: user.userUid,
      });

      setSelectedIntegrations({
        name: "",
        value: "",
      });
      setCurrentProductSelected(null);

      let allIntegrationProducts = await getAllIntegrationProducts();

      // Filter the already created ones
      let allShopifyProducts = shopifyProducts.filter(
        (tp) =>
          !allIntegrationProducts.find(
            (ip) => ip.productId === tp.id.toString()
          )
      );

      setAllIntegrationProducts(allIntegrationProducts);
      setShopifyProducts(allShopifyProducts);
      setAutoCompleteSuggestions(
        allShopifyProducts.map((p) => ({
          name: p.title,
          value: p.id.toString(),
        }))
      );
    }
  };

  async function disconnectIntegration() {
    if (process.browser && shopifyIntegration._id) {
      await ShopifyAPI.disconnectAndClearWebhooks();
      await IntegrationAPI.disconnect(shopifyIntegration._id);
      handleSelected(SettingsTab.SHOPIFY);
    }
  }

  function confirmDisconnectIntegration() {
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Disconnect Integration"
        description="Are you sure you want to disconnect this integration?"
        buttonsName={["No", "Yes"]}
        onOkClicked={async () => {
          try {
            disconnectIntegration();
          } catch (e) {
            console.log(e);
          } finally {
            modal.popModal();
          }
        }}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }

  return (
    <Container>
      <InfoColumn>
        <TextColumn>
          <Title>Shopify Products Integration</Title>
          <SubTitle>
            Please choose the products you wish to add tags from the list below.
          </SubTitle>
        </TextColumn>

        <img src="./images/shopify.png" alt="shopify" />
      </InfoColumn>

      <FormWrapper>
        <StyledAutocompleteSelectWrapper>
          <StyledAutocompleteSelect
            options={autoCompleteSuggestions}
            onOptionSelect={handleSelectedSearch}
            selectedOption={selectedIntegrations}
            placeholder="Enter the product name from Shopify"
          />
        </StyledAutocompleteSelectWrapper>

        <StyledButton onClick={handleSubmit}>Add</StyledButton>
      </FormWrapper>

      <ProductListContent>
        <Title>Products</Title>

        <ProductListWrapper>
          {allIntegrationProducts.map((product, index) => (
            <ShopifyProductItem
              // I only need the first one to load the tags
              cancelTagLoading={index !== 0}
              key={product._id}
              product={product}
              getAllIntegrationProducts={async () => {
                let allIntegrationProducts = await getAllIntegrationProducts();

                // Filter the already created ones
                let allShopifyProducts = shopifyProducts.filter(
                  (tp) =>
                    !allIntegrationProducts.find(
                      (ip) => ip.productId === tp.id.toString()
                    )
                );

                setAllIntegrationProducts(allIntegrationProducts);
                setShopifyProducts(allShopifyProducts);
                setAutoCompleteSuggestions(
                  allShopifyProducts.map((p) => ({
                    name: p.title,
                    value: p.id.toString(),
                  }))
                );
              }}
            />
          ))}
        </ProductListWrapper>
      </ProductListContent>

      <FooterColumn>
        <StyledButton
          css={css`
            background-color: transparent;
            border-color: red;
            color: red;

            &:hover {
              background-color: red;
              color: white;
            }

            transition: 300ms;
          `}
          onClick={confirmDisconnectIntegration}
        >
          Disconnect integration
        </StyledButton>
      </FooterColumn>
    </Container>
  );
}
